import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert/Alert';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Form, FormField, FormItem } from '@/components/ui/Form/Form';
import { canStudentSchedule } from '@/features/scheduling/utils/schedulability';
import { hasStudentRemainingSessions } from '@/features/scheduling/utils/hasStudentRemainingSessions';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import type { Id } from '@/types/api';
import { StudentSelectItem } from '../../../StudentSelectItem/StudentSelectItem';
import { useScheduleFlow } from '../../context/useScheduleFlow';

const isMultipleSchedulingEnabled = true;

export const SelectStudentsStep = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.selectStudentsStep' });

  const { data, goToNextStep, goToStep, updateData } = useScheduleFlow();

  const { userData } = useAuthStore();

  const userDataStudents = userData?.students ?? [];
  const configStudents = useMemo(() => (data.config.data?.students ?? []).filter((student) => canStudentSchedule(student)), [data.config.data?.students]);

  const liveRegionRef = useRef<HTMLDivElement>(null);

  const formSchema = z.object({
    selectedStudentsIds: z.array(z.number()).min(1, { message: t('noSelectError') }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      selectedStudentsIds: data.selectedStudentsIds,
    },
  });

  const selectedCount = form.watch('selectedStudentsIds')?.length ?? 0;

  const { timeZone: centerTimeZone } = useTimeFor('sessionTime');

  const { t: tStudentItem } = useTranslation('scheduling', { keyPrefix: 'studentSelectItem.accessibility' });

  const handleStudentClick = (studentId: Id, field: { value: Id[]; onChange: (value: Id[]) => void }) => {
    const currentValue = field.value ?? [];
    const isCurrentlySelected = currentValue.includes(studentId);

    let newValue: Id[];
    if (isCurrentlySelected) {
      newValue = currentValue.filter((selectedStudentId) => selectedStudentId !== studentId);
    } else if (isMultipleSchedulingEnabled) {
      newValue = [...currentValue, studentId];
    } else {
      newValue = [studentId];
    }

    field.onChange(newValue);
    updateData('hasSessionDetailsSettings', true);

    if (liveRegionRef.current) {
      const action = isCurrentlySelected ? tStudentItem('deselected') : tStudentItem('selected');
      const count = newValue.length;
      const studentsWord = count === 1 ? t('accessibility.studentWord') : t('accessibility.studentsWord');

      const studentFromUserData = userDataStudents.find((s) => s.id === studentId);

      if (!studentFromUserData) {
        throw new Error(`Configuration not found for student ${studentId}`);
      }

      liveRegionRef.current.textContent = t('accessibility.selectionAnnouncement', {
        firstName: studentFromUserData.firstName,
        lastName: studentFromUserData.lastName,
        action,
        count,
        students: studentsWord,
      });
    }
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    void captureAnalyticsEvent('scheduling_students_selected', { selectedStudentsIds: values.selectedStudentsIds });
    updateData('selectedStudentsIds', values.selectedStudentsIds);
    goToNextStep();
  };

  const handleSingleStudentAutoSelect = useCallback(() => {
    if (configStudents.length === 1) {
      updateData('selectedStudentsIds', [configStudents[0].id]);
      goToNextStep();
    }
  }, [goToNextStep, configStudents, updateData]);

  useEffect(handleSingleStudentAutoSelect, [handleSingleStudentAutoSelect]);

  const hasSessionsLeft = useMemo(
    () => configStudents.some((student) => hasStudentRemainingSessions(student, centerTimeZone)),
    [configStudents, centerTimeZone],
  );

  const handleNoSessionsLeft = useCallback(() => {
    if (!hasSessionsLeft) {
      goToStep('noSessionsLeft');
    }
  }, [goToStep, hasSessionsLeft]);

  useEffect(handleNoSessionsLeft, [handleNoSessionsLeft]);

  return (
    <>
      <DialogBody>
        <Form {...form}>
          <form
            id='select-students-form'
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
            className='flex flex-1 flex-col'
          >
            <div className='mb-6'>
              <h3 className='text-heading-m-bold text-text-default-primary mb-2'>{t('title')}</h3>
              <p className='text-body-m-regular text-text-default-secondary'>
                {t(isMultipleSchedulingEnabled && configStudents.length > 1 ? 'subheadingMultipleScheduling' : 'subheadingSingleScheduling')}
              </p>
            </div>

            <FormField
              control={form.control}
              name='selectedStudentsIds'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <fieldset
                    aria-describedby='student-list-description'
                    aria-invalid={!!form.formState.errors.selectedStudentsIds || undefined}
                    aria-errormessage={form.formState.errors.selectedStudentsIds ? 'selected-students-error' : undefined}
                    className='m-0 flex-1 border-0 p-0'
                  >
                    <legend className='sr-only'>{t('accessibility.studentListLabel')}</legend>
                    <div id='student-list-description' className='sr-only'>
                      {t('accessibility.studentListDescription')}
                    </div>

                    <div className='flex flex-wrap gap-4'>
                      {configStudents.map((student) => {
                        const isSelected = field.value?.includes(student.id) ?? false;
                        const isDisabled = !hasStudentRemainingSessions(student, centerTimeZone);

                        const studentFromUserData = userDataStudents.find((s) => s.id === student.id);

                        if (!studentFromUserData) {
                          throw new Error(`Configuration not found for student ${student.id}`);
                        }

                        return (
                          <StudentSelectItem
                            key={student.id}
                            student={studentFromUserData}
                            isSelected={isSelected}
                            isDisabled={isDisabled}
                            isMultipleSchedulingEnabled={isMultipleSchedulingEnabled}
                            onToggle={() => handleStudentClick(student.id, field)}
                          />
                        );
                      })}
                    </div>
                  </fieldset>
                </FormItem>
              )}
            />

            <div ref={liveRegionRef} role='status' aria-atomic='true' className='sr-only' />
          </form>
        </Form>
      </DialogBody>

      <DialogFooter className='flex-col'>
        {form.formState.errors.selectedStudentsIds && (
          <Alert variant='danger' className='mb-4' id='selected-students-error'>
            {form.formState.errors.selectedStudentsIds.type === 'too_small' ? (
              <>
                <AlertTitle>{t('noStudentSelectedError.title')}</AlertTitle>
                <AlertDescription>{t('noStudentSelectedError.description')}</AlertDescription>
              </>
            ) : (
              <AlertDescription>{form.formState.errors.selectedStudentsIds.message}</AlertDescription>
            )}
          </Alert>
        )}
        {isMultipleSchedulingEnabled && selectedCount > 0 && (
          <p className='text-text-default-secondary mb-0'>{selectedCount === 1 ? t('selectedSingle') : t('selectedMultiple', { count: selectedCount })}</p>
        )}
        <Button type='submit' form='select-students-form' className='w-full'>
          {t('continueButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
